/**
 * Lightweight vanilla JS carousel replacement for Slick.
 *
 * Usage:
 *   var c = new Carousel(element, { dots: true, arrows: false, autoplay: true, fade: false, infinite: true, variableWidth: false });
 *   c.goTo(index);
 *   c.destroy();
 */
(function () {
  'use strict';

  function Carousel(el, opts) {
    this.el = typeof el === 'string' ? document.querySelector(el) : el;
    if (!this.el) return;

    this.opts = Object.assign({
      dots: false,
      arrows: false,
      autoplay: false,
      autoplaySpeed: 3000,
      infinite: true,
      fade: false,
      variableWidth: false,
      swipe: true
    }, opts || {});

    this.slides = Array.from(this.el.children);
    if (this.slides.length < 2) return;

    this.current = 0;
    this._autoplayTimer = null;
    this._touchStartX = 0;
    this._touchDiffX = 0;

    this._build();
    this._bind();
    if (this.opts.autoplay) this._startAutoplay();
  }

  Carousel.prototype._build = function () {
    this.el.classList.add('carousel');

    // Track
    this.track = document.createElement('div');
    this.track.className = 'carousel-track';
    if (this.opts.fade) this.track.classList.add('carousel-fade');

    while (this.el.firstChild) {
      this.track.appendChild(this.el.firstChild);
    }
    this.el.appendChild(this.track);

    // Wrap slides
    this.slides.forEach(function (slide, i) {
      slide.classList.add('carousel-slide');
      if (i === 0) slide.classList.add('is-active');
    });

    if (this.opts.fade) {
      this.slides.forEach(function (s) { s.style.opacity = '0'; });
      this.slides[0].style.opacity = '1';
    } else {
      this._setPosition(false);
    }

    // Dots
    if (this.opts.dots) {
      this.dotsWrap = document.createElement('ul');
      this.dotsWrap.className = 'carousel-dots';
      for (var i = 0; i < this.slides.length; i++) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.setAttribute('type', 'button');
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.setAttribute('tabindex', '-1');
        if (i === 0) li.classList.add('is-active');
        li.appendChild(btn);
        this.dotsWrap.appendChild(li);
      }
      this.el.appendChild(this.dotsWrap);
    }

    // Arrows
    if (this.opts.arrows) {
      this.prevBtn = document.createElement('button');
      this.prevBtn.className = 'carousel-arrow carousel-prev';
      this.prevBtn.setAttribute('type', 'button');
      this.prevBtn.setAttribute('aria-label', 'Previous');
      this.prevBtn.innerHTML = '&#8249;';

      this.nextBtn = document.createElement('button');
      this.nextBtn.className = 'carousel-arrow carousel-next';
      this.nextBtn.setAttribute('type', 'button');
      this.nextBtn.setAttribute('aria-label', 'Next');
      this.nextBtn.innerHTML = '&#8250;';

      this.el.appendChild(this.prevBtn);
      this.el.appendChild(this.nextBtn);
    }
  };

  Carousel.prototype._bind = function () {
    var self = this;

    // Dots
    if (this.opts.dots && this.dotsWrap) {
      this.dotsWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('li');
        if (!btn) return;
        var idx = Array.from(self.dotsWrap.children).indexOf(btn);
        if (idx >= 0) self.goTo(idx);
      });
    }

    // Arrows
    if (this.opts.arrows) {
      this.prevBtn.addEventListener('click', function () { self.prev(); });
      this.nextBtn.addEventListener('click', function () { self.next(); });
    }

    this.el.addEventListener('click', function (e) {
      if (e.target.closest('.carousel-dots, .carousel-arrow, a, button')) return;
      var rect = self.el.getBoundingClientRect();
      var x = e.clientX - rect.left;
      if (x <= rect.width * 0.25) {
        self.prev();
      } else if (x >= rect.width * 0.75) {
        self.next();
      }
    });

    // Swipe
    if (this.opts.swipe) {
      this.el.addEventListener('touchstart', function (e) {
        self._touchStartX = e.touches[0].clientX;
        self._touchDiffX = 0;
        self._stopAutoplay();
      }, { passive: true });

      this.el.addEventListener('touchmove', function (e) {
        self._touchDiffX = e.touches[0].clientX - self._touchStartX;
      }, { passive: true });

      this.el.addEventListener('touchend', function () {
        if (Math.abs(self._touchDiffX) > 40) {
          if (self._touchDiffX < 0) self.next(); else self.prev();
        }
        if (self.opts.autoplay) self._startAutoplay();
      });
    }
  };

  Carousel.prototype._setPosition = function (animate) {
    if (this.opts.fade) return;
    var offset;
    if (this.opts.variableWidth) {
      offset = 0;
      for (var i = 0; i < this.current; i++) {
        offset += this.slides[i].offsetWidth;
      }
      this.track.style.transition = animate !== false ? 'transform 0.35s ease' : 'none';
      this.track.style.transform = 'translateX(-' + offset + 'px)';
    } else {
      this.track.style.transition = animate !== false ? 'transform 0.35s ease' : 'none';
      this.track.style.transform = 'translateX(-' + (this.current * 100) + '%)';
    }
  };

  Carousel.prototype.goTo = function (index) {
    var len = this.slides.length;
    if (this.opts.infinite) {
      index = ((index % len) + len) % len;
    } else {
      index = Math.max(0, Math.min(index, len - 1));
    }

    this.slides[this.current].classList.remove('is-active');
    if (this.opts.fade) this.slides[this.current].style.opacity = '0';

    this.current = index;

    this.slides[this.current].classList.add('is-active');
    if (this.opts.fade) {
      this.slides[this.current].style.opacity = '1';
    } else {
      this._setPosition(true);
    }

    if (this.opts.dots && this.dotsWrap) {
      var dots = this.dotsWrap.children;
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === this.current);
      }
    }
  };

  Carousel.prototype.next = function () { this.goTo(this.current + 1); };
  Carousel.prototype.prev = function () { this.goTo(this.current - 1); };

  Carousel.prototype._startAutoplay = function () {
    var self = this;
    this._stopAutoplay();
    this._autoplayTimer = setInterval(function () { self.next(); }, this.opts.autoplaySpeed);
  };

  Carousel.prototype._stopAutoplay = function () {
    if (this._autoplayTimer) { clearInterval(this._autoplayTimer); this._autoplayTimer = null; }
  };

  Carousel.prototype.destroy = function () {
    this._stopAutoplay();
    if (this.dotsWrap) this.dotsWrap.remove();
    if (this.prevBtn) this.prevBtn.remove();
    if (this.nextBtn) this.nextBtn.remove();
    // Unwrap track
    while (this.track.firstChild) {
      this.el.appendChild(this.track.firstChild);
    }
    this.track.remove();
    this.el.classList.remove('carousel');
    this.slides.forEach(function (s) { s.classList.remove('carousel-slide', 'is-active'); s.style.opacity = ''; });
  };

  window.Carousel = Carousel;
})();
